import React from "react";
import logo from "../images/logo-dark.png";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import LiveTvRoundedIcon from "@mui/icons-material/LiveTvRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import TourRoundedIcon from "@mui/icons-material/TourRounded";
import {CUSTOMER_KEY, INTEGRATION} from "../../constants/constants";
import UserMenu from "./UserMenu";

const HeaderAction = ({icon, label, href, onClick, analyticsSupport, analyticsLinkType}) => {
    const baseClasses = "inline-flex items-center gap-2 rounded-full border border-indigo-100 bg-indigo-50 px-3 py-2 text-xs font-semibold text-indigo-600 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-100";

    if (href) {
        return (
            <a
                className={baseClasses}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                data-analytics-support={analyticsSupport}
                data-analytics-link-type={analyticsLinkType}
            >
                <span className="flex h-6 w-6 items-center justify-center rounded-full text-indigo-600">
                    {icon}
                </span>
                <span className="hidden sm:block">{label}</span>
            </a>
        );
    }

    return (
        <button
            type="button"
            onClick={onClick}
            className={baseClasses}
            aria-label={label}
            data-analytics-support={analyticsSupport}
            data-analytics-link-type={analyticsLinkType}
        >
            <span className="flex h-6 w-6 items-center justify-center rounded-full text-indigo-600">
                {icon}
            </span>
            <span className="hidden sm:block">{label}</span>
        </button>
    );
};

// `showUserMenu` is only set inside the logged-in app shell (not on login,
// onboarding or redirect pages, which share this header). `onOpenProductTour`
// adds the "Produkttour" entry to the help actions (only once the tour is
// available for the customer).
const Header = ({contract, onOpenMobileNav, showUserMenu = false, onOpenProductTour, productTourLabel = "Product tour"}) => {
    const getSupportLinkBySystem = () => {
        const customerId = sessionStorage.getItem(CUSTOMER_KEY) || localStorage.getItem(CUSTOMER_KEY) || "";
        const integration = sessionStorage.getItem(INTEGRATION) || localStorage.getItem(INTEGRATION) || "";

        const subjectBase = () => {
            if (!contract) {
                return `Support Anfrage Registrierung customerId ${customerId}`;
            }

            if (contract?.contractType === "STRIPE") {
                return `Support Anfrage SepaHeld STR customerId ${customerId} integration ${integration}`;
            }

            return `Support Anfrage SepaHeld Lexware Office customerId ${customerId} integration ${integration}`;
        };

        return `mailto:support@sepaheld.de?subject=${encodeURIComponent(subjectBase())}`;
    };

    const handleSupportClick = () => {
        window.open(getSupportLinkBySystem(), "_blank");
    };

    const hasMobileNavTrigger = typeof onOpenMobileNav === "function";
    const hasProductTour = typeof onOpenProductTour === "function";

    const handleOpenMobileNav = () => {
        if (hasMobileNavTrigger) {
            onOpenMobileNav();
        }
    };

    return (
        <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/80 backdrop-blur-sm">
            <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
                <div className="flex items-center gap-3">
                    <img src={logo} alt="SepaHeld" className="h-10 w-auto"/>
                    <div className="hidden sm:flex flex-col">
                        <span className="text-xs font-semibold uppercase tracking-[0.35em] text-indigo-500">SepaHeld</span>
                        <span className="text-sm font-semibold text-slate-900">Finance Control Center</span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {hasMobileNavTrigger && (
                        <button
                            type="button"
                            onClick={handleOpenMobileNav}
                            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-indigo-200 hover:text-indigo-600 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-indigo-500 md:hidden"
                            aria-label="Open navigation"
                        >
                            <MenuRoundedIcon fontSize="small"/>
                        </button>
                    )}
                    {hasProductTour && (
                        <button
                            type="button"
                            onClick={() => onOpenProductTour()}
                            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-indigo-100 bg-white text-indigo-600 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-indigo-500 sm:hidden"
                            aria-label={productTourLabel}
                            data-testid="header-product-tour-mobile"
                        >
                            <TourRoundedIcon fontSize="small"/>
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={handleSupportClick}
                        data-analytics-support="header_mobile"
                        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-indigo-100 bg-white text-indigo-600 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-indigo-500 sm:hidden"
                        aria-label="Contact support"
                    >
                        <ChatBubbleOutlineRoundedIcon fontSize="small"/>
                    </button>
                    <div className="hidden items-center gap-2 sm:flex">
                        {hasProductTour && (
                            <HeaderAction
                                icon={<TourRoundedIcon fontSize="small"/>}
                                label={productTourLabel}
                                onClick={() => onOpenProductTour()}
                            />
                        )}
                        <HeaderAction
                            icon={<HelpOutlineRoundedIcon fontSize="small"/>}
                            label="Help Center"
                            href="https://sepaheld.com/f_a_q"
                            analyticsSupport="header_help_center"
                            analyticsLinkType="help_center"
                        />
                        <HeaderAction
                            icon={<LiveTvRoundedIcon fontSize="small"/>}
                            label="Video Tour"
                            href="https://sepaheld.com/f_a_q"
                            analyticsSupport="header_video_tour"
                            analyticsLinkType="video_tour"
                        />
                        <HeaderAction
                            icon={<ChatBubbleOutlineRoundedIcon fontSize="small"/>}
                            label="Contact Support"
                            onClick={handleSupportClick}
                            analyticsSupport="header_contact"
                        />
                    </div>
                    {showUserMenu && <UserMenu/>}
                </div>
            </div>
        </header>
    );
};

export default Header;
